import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Tooltip,
  CircularProgress,
  Snackbar,
  Alert,
} from '@mui/material';
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  Edit as EditIcon,
  Refresh as RefreshIcon,
  ToggleOn as ToggleOnIcon,
  ToggleOff as ToggleOffIcon,
  Person as PersonIcon,
  Warning as WarningIcon,
} from '@mui/icons-material';
import api from '../../api/axios';
import { RdpUser, ClientDashboardStats } from '../../types';
import { useTranslation } from 'react-i18next';

interface CreateUserForm {
  username: string;
  password: string;
}

interface EditUserForm {
  username: string;
  password: string;
}

const initialForm: CreateUserForm = {
  username: '',
  password: '',
};

const initialEditForm: EditUserForm = {
  username: '',
  password: '',
};

const Users: React.FC = () => {
  const { t } = useTranslation();
  const [users, setUsers] = useState<RdpUser[]>([]);
  const [stats, setStats] = useState<ClientDashboardStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [form, setForm] = useState<CreateUserForm>(initialForm);
  const [formErrors, setFormErrors] = useState<Partial<CreateUserForm>>({});
  const [submitting, setSubmitting] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editForm, setEditForm] = useState<EditUserForm>(initialEditForm);
  const [editFormErrors, setEditFormErrors] = useState<Partial<EditUserForm>>({});
  const [editingUserId, setEditingUserId] = useState<number | null>(null);
  const [snackbar, setSnackbar] = useState<{
    open: boolean;
    message: string;
    severity: 'success' | 'error' | 'warning';
  }>({ open: false, message: '', severity: 'success' });

  useEffect(() => {
    fetchUsers();
    fetchStats();
  }, []);

  const fetchUsers = async () => {
    try {
      setLoading(true);
      const response = await api.get<RdpUser[]>('/client/users');
      setUsers(response.data);
    } catch (err: any) {
      showSnackbar(err.response?.data?.message || t('client.users.fetchFailed'), 'error');
    } finally {
      setLoading(false);
    }
  };

  const fetchStats = async () => {
    try {
      const response = await api.get<ClientDashboardStats>('/client/dashboard');
      setStats(response.data);
    } catch (err: any) {
      console.error('Failed to fetch stats:', err);
    }
  };

  const showSnackbar = (message: string, severity: 'success' | 'error' | 'warning') => {
    setSnackbar({ open: true, message, severity });
  };

  const validateForm = (): boolean => {
    const errors: Partial<CreateUserForm> = {};
    if (!form.username.trim()) errors.username = t('client.users.usernameRequired');
    if (form.username.length < 3 && form.username.trim()) {
      errors.username = t('client.users.usernameMin');
    }
    if (!form.password.trim()) errors.password = t('client.users.passwordRequired');
    if (form.password.length < 6 && form.password.trim()) {
      errors.password = t('client.users.passwordMin');
    }

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleCreate = async () => {
    if (!validateForm()) return;

    if (stats && stats.totalUsers >= stats.maxUsers) {
      showSnackbar(t('client.users.maxReached'), 'warning');
      return;
    }

    setSubmitting(true);
    try {
      await api.post('/client/users', {
        username: form.username,
        password: form.password,
      });
      showSnackbar(t('client.users.createdSuccess'), 'success');
      setDialogOpen(false);
      setForm(initialForm);
      setFormErrors({});
      fetchUsers();
      fetchStats();
    } catch (err: any) {
      showSnackbar(err.response?.data?.message || t('client.users.createFailed'), 'error');
    } finally {
      setSubmitting(false);
    }
  };

  const handleToggleActive = async (id: number, currentActive: boolean) => {
    try {
      await api.put(`/client/users/${id}/toggle`);
      showSnackbar(
        currentActive ? t('client.users.deactivatedSuccess') : t('client.users.activatedSuccess'),
        'success'
      );
      fetchUsers();
    } catch (err: any) {
      showSnackbar(err.response?.data?.message || t('client.users.toggleFailed'), 'error');
    }
  };

  const handleDelete = async (id: number) => {
    if (!window.confirm(t('client.users.deleteConfirm'))) return;

    try {
      await api.delete(`/client/users/${id}`);
      showSnackbar(t('client.users.deletedSuccess'), 'success');
      fetchUsers();
      fetchStats();
    } catch (err: any) {
      showSnackbar(err.response?.data?.message || t('client.users.deleteFailed'), 'error');
    }
  };

  const handleInputChange = (field: keyof CreateUserForm) => (
    e: React.ChangeEvent<HTMLInputElement>
  ) => {
    setForm({ ...form, [field]: e.target.value });
    if (formErrors[field]) {
      setFormErrors({ ...formErrors, [field]: undefined });
    }
  };

  const handleEditOpen = (user: RdpUser) => {
    setEditingUserId(user.id);
    setEditForm({ username: user.username, password: '' });
    setEditFormErrors({});
    setEditDialogOpen(true);
  };

  const validateEditForm = (): boolean => {
    const errors: Partial<EditUserForm> = {};
    if (!editForm.username.trim()) errors.username = t('client.users.usernameRequired');
    if (editForm.username.trim() && editForm.username.length < 3) {
      errors.username = t('client.users.usernameMin');
    }
    if (editForm.password.trim() && editForm.password.length < 6) {
      errors.password = t('client.users.passwordMin');
    }
    setEditFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleUpdate = async () => {
    if (!validateEditForm() || editingUserId === null) return;

    setSubmitting(true);
    try {
      await api.put(`/client/users/${editingUserId}`, {
        username: editForm.username,
        password: editForm.password || undefined,
      });
      showSnackbar(t('client.users.updatedSuccess'), 'success');
      setEditDialogOpen(false);
      setEditForm(initialEditForm);
      setEditFormErrors({});
      setEditingUserId(null);
      fetchUsers();
    } catch (err: any) {
      showSnackbar(err.response?.data?.message || t('client.users.updateFailed'), 'error');
    } finally {
      setSubmitting(false);
    }
  };

  const handleEditInputChange = (field: keyof EditUserForm) => (
    e: React.ChangeEvent<HTMLInputElement>
  ) => {
    setEditForm({ ...editForm, [field]: e.target.value });
    if (editFormErrors[field]) {
      setEditFormErrors({ ...editFormErrors, [field]: undefined });
    }
  };

  const isAtLimit = stats ? stats.totalUsers >= stats.maxUsers : false;
  const isNearLimit = stats ? stats.totalUsers >= stats.maxUsers * 0.8 : false;

  if (loading) {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          minHeight: '60vh',
        }}
      >
        <CircularProgress size={48} sx={{ color: '#1a237e' }} />
      </Box>
    );
  }

  return (
    <Box>
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          mb: 3,
        }}
      >
        <Box>
          <Typography variant="h4" sx={{ fontWeight: 700, color: '#1a237e', mb: 0.5 }}>
            {t('client.users.title')}
          </Typography>
          <Typography variant="body1" sx={{ color: '#78909c' }}>
            {t('client.users.subtitle')}
            {stats && (
              <Chip
                label={t('client.users.usersCount', { current: stats.totalUsers, max: stats.maxUsers })}
                size="small"
                sx={{
                  ml: 1,
                  fontWeight: 600,
                  backgroundColor: isAtLimit ? '#ffebee' : isNearLimit ? '#fff3e0' : '#e8f5e9',
                  color: isAtLimit ? '#c62828' : isNearLimit ? '#e65100' : '#2e7d32',
                }}
              />
            )}
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Tooltip title={t('common.refresh')}>
            <IconButton onClick={fetchUsers} sx={{ color: '#1a237e' }}>
              <RefreshIcon />
            </IconButton>
          </Tooltip>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => {
              if (isAtLimit) {
                showSnackbar(
                  t('client.users.maxReached'),
                  'warning'
                );
                return;
              }
              setDialogOpen(true);
            }}
            disabled={isAtLimit}
            sx={{
              textTransform: 'none',
              fontWeight: 600,
              borderRadius: 2,
              px: 3,
              background: isAtLimit
                ? '#bdbdbd'
                : 'linear-gradient(135deg, #1a237e 0%, #0d47a1 100%)',
              boxShadow: isAtLimit ? 'none' : '0 4px 15px rgba(26,35,126,0.3)',
              '&:hover': {
                background: isAtLimit
                  ? '#bdbdbd'
                  : 'linear-gradient(135deg, #283593 0%, #1565c0 100%)',
              },
            }}
          >
            {t('client.users.addUser')}
          </Button>
        </Box>
      </Box>

      {isNearLimit && !isAtLimit && (
        <Alert
          severity="warning"
          icon={<WarningIcon />}
          sx={{ mb: 3, borderRadius: 2 }}
        >
          {t('client.users.nearLimit', { current: stats?.totalUsers, max: stats?.maxUsers })}
        </Alert>
      )}

      {isAtLimit && (
        <Alert severity="error" sx={{ mb: 3, borderRadius: 2 }}>
          {t('client.users.atLimit', { max: stats?.maxUsers })}
        </Alert>
      )}

      <TableContainer
        component={Paper}
        sx={{
          borderRadius: 3,
          boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
        }}
      >
        <Table>
          <TableHead>
            <TableRow
              sx={{
                backgroundColor: '#f8f9fa',
                '& th': {
                  fontWeight: 700,
                  color: '#37474f',
                  fontSize: '0.85rem',
                  borderBottom: '2px solid #e0e0e0',
                },
              }}
            >
              <TableCell>{t('client.users.username')}</TableCell>
              <TableCell>{t('client.users.windowsUsername')}</TableCell>
              <TableCell>{t('common.status')}</TableCell>
              <TableCell>{t('client.users.created')}</TableCell>
              <TableCell align="center">{t('common.actions')}</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {users.length === 0 ? (
              <TableRow>
                <TableCell colSpan={5} align="center" sx={{ py: 6 }}>
                  <PersonIcon sx={{ fontSize: 48, color: '#bdbdbd', mb: 1 }} />
                  <Typography variant="body1" sx={{ color: '#9e9e9e' }}>
                    {t('client.users.noUsers')}
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              users.map((user) => (
                <TableRow
                  key={user.id}
                  sx={{
                    '&:hover': { backgroundColor: '#f5f7fa' },
                    transition: 'background-color 0.2s',
                  }}
                >
                  <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <PersonIcon sx={{ fontSize: 20, color: '#1a237e' }} />
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>
                        {user.username}
                      </Typography>
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                      {user.windowsUsername}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={user.isActive ? t('common.active') : t('common.inactive')}
                      color={user.isActive ? 'success' : 'error'}
                      size="small"
                      sx={{ fontWeight: 600, fontSize: '0.75rem' }}
                    />
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" sx={{ color: '#78909c' }}>
                      {new Date(user.createdAt).toLocaleDateString()}
                    </Typography>
                  </TableCell>
                  <TableCell align="center">
                    <Tooltip title={user.isActive ? t('client.users.deactivate') : t('client.users.activate')}>
                      <IconButton
                        onClick={() => handleToggleActive(user.id, user.isActive)}
                        size="small"
                        sx={{
                          color: user.isActive ? '#4caf50' : '#9e9e9e',
                          '&:hover': {
                            backgroundColor: user.isActive ? '#e8f5e9' : '#f5f5f5',
                          },
                        }}
                      >
                        {user.isActive ? (
                          <ToggleOnIcon fontSize="small" />
                        ) : (
                          <ToggleOffIcon fontSize="small" />
                        )}
                      </IconButton>
                    </Tooltip>
                    <Tooltip title={t('client.users.editUser')}>
                      <IconButton
                        onClick={() => handleEditOpen(user)}
                        size="small"
                        sx={{
                          color: '#1a237e',
                          '&:hover': { backgroundColor: '#e8eaf6' },
                        }}
                      >
                        <EditIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title={t('client.users.deleteUser')}>
                      <IconButton
                        onClick={() => handleDelete(user.id)}
                        size="small"
                        sx={{
                          color: '#ef5350',
                          '&:hover': { backgroundColor: '#ffebee' },
                        }}
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <Dialog
        open={dialogOpen}
        onClose={() => {
          setDialogOpen(false);
          setForm(initialForm);
          setFormErrors({});
        }}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: { borderRadius: 3 },
        }}
      >
        <DialogTitle
          sx={{
            background: 'linear-gradient(135deg, #1a237e 0%, #0d47a1 100%)',
            color: '#ffffff',
            fontWeight: 700,
          }}
        >
          {t('client.users.addNewUser')}
        </DialogTitle>
        <DialogContent sx={{ pt: 3, mt: 1 }}>
          {isNearLimit && (
            <Alert severity="warning" sx={{ mb: 2, borderRadius: 2 }}>
              {t('client.users.limitWarning', { current: stats?.totalUsers, max: stats?.maxUsers })}
            </Alert>
          )}
          <TextField
            fullWidth
            label={t('client.users.username')}
            value={form.username}
            onChange={handleInputChange('username')}
            error={!!formErrors.username}
            helperText={formErrors.username}
            sx={{ mb: 2 }}
          />
          <TextField
            fullWidth
            label={t('common.password')}
            type="password"
            value={form.password}
            onChange={handleInputChange('password')}
            error={!!formErrors.password}
            helperText={formErrors.password}
          />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 3 }}>
          <Button
            onClick={() => {
              setDialogOpen(false);
              setForm(initialForm);
              setFormErrors({});
            }}
            sx={{ textTransform: 'none', fontWeight: 500 }}
          >
            {t('common.cancel')}
          </Button>
          <Button
            variant="contained"
            onClick={handleCreate}
            disabled={submitting}
            sx={{
              textTransform: 'none',
              fontWeight: 600,
              borderRadius: 2,
              px: 3,
              background: 'linear-gradient(135deg, #1a237e 0%, #0d47a1 100%)',
              '&:hover': {
                background: 'linear-gradient(135deg, #283593 0%, #1565c0 100%)',
              },
            }}
          >
            {submitting ? <CircularProgress size={24} sx={{ color: '#fff' }} /> : t('client.users.createUser')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Edit User Dialog */}
      <Dialog
        open={editDialogOpen}
        onClose={() => {
          setEditDialogOpen(false);
          setEditForm(initialEditForm);
          setEditFormErrors({});
          setEditingUserId(null);
        }}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: { borderRadius: 3 },
        }}
      >
        <DialogTitle
          sx={{
            background: 'linear-gradient(135deg, #1a237e 0%, #0d47a1 100%)',
            color: '#ffffff',
            fontWeight: 700,
          }}
        >
          {t('client.users.editUserTitle')}
        </DialogTitle>
        <DialogContent sx={{ pt: 3, mt: 1 }}>
          <TextField
            fullWidth
            label={t('client.users.username')}
            value={editForm.username}
            onChange={handleEditInputChange('username')}
            error={!!editFormErrors.username}
            helperText={editFormErrors.username}
            sx={{ mb: 2 }}
          />
          <TextField
            fullWidth
            label={t('common.password')}
            type="password"
            value={editForm.password}
            onChange={handleEditInputChange('password')}
            error={!!editFormErrors.password}
            helperText={editFormErrors.password || t('client.users.passwordHint')}
          />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 3 }}>
          <Button
            onClick={() => {
              setEditDialogOpen(false);
              setEditForm(initialEditForm);
              setEditFormErrors({});
              setEditingUserId(null);
            }}
            sx={{ textTransform: 'none', fontWeight: 500 }}
          >
            {t('common.cancel')}
          </Button>
          <Button
            variant="contained"
            onClick={handleUpdate}
            disabled={submitting}
            sx={{
              textTransform: 'none',
              fontWeight: 600,
              borderRadius: 2,
              px: 3,
              background: 'linear-gradient(135deg, #1a237e 0%, #0d47a1 100%)',
              '&:hover': {
                background: 'linear-gradient(135deg, #283593 0%, #1565c0 100%)',
              },
            }}
          >
            {submitting ? <CircularProgress size={24} sx={{ color: '#fff' }} /> : t('common.save')}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={5000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
          variant="filled"
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default Users;
